import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps'
import baseUrl from '../../Utils/baseUrl'

const US_TOPO = '/us-states-10m.json'

type Location = {
  id: number
  uid: string
  name: string
  city: string | null
  state: string | null
  zipcode: string | null
  address: string | null
  phone: string | null
  category: string | null
  ring_group: string | null
  site: string | null
  latitude: number | null
  longitude: number | null
  listed: boolean | null
  rank: number | null
  page: number | null
  calls: number
  calls_60d: number
}

type Funnel = { leads?: number; booked?: number; arrived?: number }
type TopCity = { city: string; state: string | null; live_count: number; calls: number } & Funnel
type TopState = { state: string; live_count: number; calls: number } & Funnel
type TopFacility = { name: string; city: string | null; state: string | null; phone: string | null; ring_group: string | null; calls: number } & Funnel
type TopPhone = { phone: string; name: string | null; city: string | null; state: string | null; ring_group: string | null; calls: number } & Funnel
type TopRingGroup = { ring_group: string; live_count: number; calls: number } & Funnel

type PanelView = 'cities' | 'facilities' | 'phones' | 'ring_groups'

type GeoAnalyticsSectionProps = {
  /** Inclusive start date (YYYY-MM-DD) or null to use the baked 60-day snapshot */
  from?: string | null
  /** Exclusive end date (YYYY-MM-DD) or null to use the baked 60-day snapshot */
  to?: string | null
  /** Display label for the selected range (e.g., "Last 7 days") */
  rangeLabel?: string
}

type Stats = {
  range?: { from: string | null; to: string | null }
  totals: {
    live_total: number
    sites_total?: number
    live_geocoded: number
    live_with_calls: number
    calls_total: number
    calls_60d_total: number
    leads?: number
    booked?: number
    arrived?: number
  }
  top_cities: TopCity[]
  top_states: TopState[]
  top_facilities?: TopFacility[]
  top_phones?: TopPhone[]
  top_ring_groups?: TopRingGroup[]
}

const formatNumber = (n: number) => n.toLocaleString()

const StatTile: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="bg-dark-card border border-dark-border rounded-xl px-5 py-4">
    <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</div>
    <div className="text-2xl font-semibold text-gray-100">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
)

type PanelRow = {
  key: string
  title: string
  subtitle?: string
  calls: number
  leads?: number
  booked?: number
  arrived?: number
  liveCount?: number
  /** Predicate: which locations match this row */
  match: (loc: Location) => boolean
}

const VIEW_OPTIONS: { value: PanelView; label: string; description: string }[] = [
  { value: 'cities',      label: 'Top Cities',       description: 'Where your callers came from' },
  { value: 'facilities',  label: 'Site Name',        description: 'Top sites by call volume' },
  { value: 'phones',      label: 'Phone Numbers',    description: 'Top site phone numbers' },
  { value: 'ring_groups', label: 'Ring Groups',      description: 'Where calls were routed' },
]

const _ci = (s: string | null | undefined) => (s || '').trim().toLowerCase()

const toRows = (view: PanelView, stats: Stats | null): PanelRow[] => {
  if (!stats) return []
  if (view === 'cities') {
    return (stats.top_cities || []).map(c => {
      const city = _ci(c.city)
      const state = _ci(c.state)
      return {
        key: `${c.city}-${c.state}`,
        title: `${c.city}${c.state ? `, ${c.state}` : ''}`,
        subtitle: `${c.live_count} site${c.live_count === 1 ? '' : 's'}`,
        calls: Number(c.calls) || 0,
        leads: Number(c.leads) || 0,
        booked: Number(c.booked) || 0,
        arrived: Number(c.arrived) || 0,
        liveCount: Number(c.live_count) || 0,
        match: (loc) => _ci(loc.city) === city && _ci(loc.state) === state,
      }
    })
  }
  if (view === 'facilities') {
    return (stats.top_facilities || []).map((f, i) => {
      const name = _ci(f.name)
      const phone = _ci(f.phone)
      return {
        key: `${f.name}-${i}`,
        title: f.name,
        subtitle: [f.city, f.state].filter(Boolean).join(', ') || (f.phone || ''),
        calls: Number(f.calls) || 0,
        leads: Number(f.leads) || 0,
        booked: Number(f.booked) || 0,
        arrived: Number(f.arrived) || 0,
        match: (loc) => _ci(loc.name) === name && (!phone || _ci(loc.phone) === phone),
      }
    })
  }
  if (view === 'phones') {
    return (stats.top_phones || []).map((p, i) => {
      const phone = _ci(p.phone)
      return {
        key: `${p.phone}-${i}`,
        title: p.phone,
        subtitle: p.name || [p.city, p.state].filter(Boolean).join(', '),
        calls: Number(p.calls) || 0,
        leads: Number(p.leads) || 0,
        booked: Number(p.booked) || 0,
        arrived: Number(p.arrived) || 0,
        match: (loc) => _ci(loc.phone) === phone,
      }
    })
  }
  // ring_groups
  return (stats.top_ring_groups || []).map(r => {
    const rg = _ci(r.ring_group)
    return {
      key: r.ring_group,
      title: r.ring_group,
      subtitle: `${r.live_count} site${r.live_count === 1 ? '' : 's'}`,
      calls: Number(r.calls) || 0,
      leads: Number(r.leads) || 0,
      booked: Number(r.booked) || 0,
      arrived: Number(r.arrived) || 0,
      liveCount: Number(r.live_count) || 0,
      match: (loc) => _ci(loc.ring_group) === rg,
    }
  })
}

const PAGE_SIZE = 8

type RowStatus = 'live' | 'dead' | 'mixed' | 'unknown'

const TopPanel: React.FC<{
  view: PanelView
  onViewChange: (v: PanelView) => void
  stats: Stats | null
  locations: Location[]
  selectedKey: string | null
  onSelectRow: (row: PanelRow | null) => void
}> = ({ view, onViewChange, stats, locations, selectedKey, onSelectRow }) => {
  const option = VIEW_OPTIONS.find(o => o.value === view)!
  const allRows = toRows(view, stats)
  const [page, setPage] = useState(0)
  const [sortMode, setSortMode] = useState<
    'default' | 'leads_desc' | 'leads_asc' | 'booked_desc' | 'booked_asc' | 'arrived_desc' | 'arrived_asc'
  >('default')

  // Reset to first page when the view or sort changes
  useEffect(() => { setPage(0) }, [view, sortMode])

  const rows = (() => {
    if (sortMode === 'default') return allRows
    const [field, dir] = sortMode.split('_') as ['leads' | 'booked' | 'arrived', 'desc' | 'asc']
    return [...allRows].sort((a, b) => {
      const diff = (a[field] ?? 0) - (b[field] ?? 0)
      return dir === 'desc' ? -diff : diff
    })
  })()

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const pageRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)
  const startIdx = safePage * PAGE_SIZE
  const endIdx = Math.min(startIdx + pageRows.length, rows.length)

  const rowStatus = (row: PanelRow): RowStatus => {
    let live = 0, dead = 0
    for (const loc of locations) {
      if (!row.match(loc)) continue
      if (loc.listed === true) live++
      else if (loc.listed === false) dead++
    }
    if (live === 0 && dead === 0) return 'unknown'
    if (dead > 0 && live === 0) return 'dead'
    if (dead > 0 && live > 0) return 'mixed'
    return 'live'
  }

  const emptyMsg = view === 'ring_groups'
    ? 'No ring group data yet.'
    : 'No calls in this period yet.'

  return (
    <div className="bg-dark-card border border-dark-border rounded-xl flex flex-col h-full">
      <div className="px-5 py-4 border-b border-dark-border">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-3 min-w-0">
            <div className="text-base font-semibold text-gray-100 truncate">{option.label}</div>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as typeof sortMode)}
              className={`text-[11px] rounded-md border px-2 py-0.5 focus:outline-none transition-colors ${
                sortMode !== 'default'
                  ? 'bg-blue-500/20 border-blue-400/60 text-blue-300'
                  : 'bg-dark-card-hover border-dark-border text-gray-400 hover:text-gray-200 focus:border-yellow-400/50'
              }`}
            >
              <option value="default" className="bg-dark-card text-gray-200">Sort</option>
              <option value="leads_desc" className="bg-dark-card text-gray-200">Most leads</option>
              <option value="leads_asc" className="bg-dark-card text-gray-200">Least leads</option>
              <option value="booked_desc" className="bg-dark-card text-gray-200">Most booked</option>
              <option value="booked_asc" className="bg-dark-card text-gray-200">Least booked</option>
              <option value="arrived_desc" className="bg-dark-card text-gray-200">Most arrived</option>
              <option value="arrived_asc" className="bg-dark-card text-gray-200">Least arrived</option>
            </select>
          </div>
          <select
            value={view}
            onChange={(e) => onViewChange(e.target.value as PanelView)}
            className="bg-dark-card-hover border border-dark-border text-gray-200 text-xs rounded-md px-2 py-1 focus:outline-none focus:border-yellow-400/50"
          >
            {VIEW_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-gray-500">{option.description}</div>
      </div>
      <div className="flex-1 overflow-hidden divide-y divide-dark-border">
        {rows.length === 0 && (
          <div className="px-5 py-8 text-sm text-gray-500 text-center">{emptyMsg}</div>
        )}
        {pageRows.map((r, i) => {
          const rank = startIdx + i + 1
          const isSelected = selectedKey === r.key
          const status = rowStatus(r)
          const titleClass = isSelected ? 'text-white' : 'text-gray-200'
          const selectedBg =
            isSelected
              ? (status === 'dead' ? 'bg-red-500/10' : 'bg-yellow-400/10')
              : 'hover:bg-dark-card-hover'
          const badge = null
          const statusDot =
            status === 'mixed' ? (
              <svg viewBox="0 0 10 10" className="w-2 h-2 flex-shrink-0" aria-hidden>
                <circle cx="5" cy="5" r="5" fill="#facc15" />
                <path d="M5 0 A5 5 0 0 0 5 10 Z" fill="#ef4444" />
              </svg>
            ) : (
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                status === 'dead' ? 'bg-red-500'
                : status === 'unknown' ? 'bg-gray-500'
                : isSelected ? 'bg-yellow-300' : 'bg-yellow-400'
              }`} />
            )
          return (
            <button
              key={r.key}
              onClick={() => onSelectRow(isSelected ? null : r)}
              className={`w-full text-left px-5 py-2.5 transition-colors ${selectedBg}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tabular-nums text-gray-500 w-6 flex-shrink-0">#{rank}</span>
                    {statusDot}
                    <span className={`text-sm font-medium truncate ${titleClass}`}>{r.title}</span>
                    {badge}
                  </div>
                  {r.subtitle && (
                    <div className="text-xs text-gray-500 mt-0.5 ml-9 truncate">
                      {r.calls} calls{r.subtitle ? ` · ${r.subtitle}` : ''}
                    </div>
                  )}
                  <div className="text-xs mt-1 ml-9 flex items-center gap-2 tabular-nums">
                    <span className="text-blue-400">{r.leads ?? 0} leads</span>
                    <span className="text-gray-600">·</span>
                    <span className="text-blue-400">{r.booked ?? 0} booked</span>
                    <span className="text-gray-600">·</span>
                    <span className="text-blue-400">{r.arrived ?? 0} arrived</span>
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {rows.length > PAGE_SIZE && (
        <div className="px-5 py-3 border-t border-dark-border flex items-center justify-between text-xs text-gray-400">
          <div>
            {startIdx + 1}–{endIdx} of {rows.length}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="px-2.5 py-1 rounded-md border border-dark-border bg-dark-card-hover text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              ‹
            </button>
            <span className="tabular-nums px-2">
              {safePage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="px-2.5 py-1 rounded-md border border-dark-border bg-dark-card-hover text-gray-300 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const GeoAnalyticsSection: React.FC<GeoAnalyticsSectionProps> = ({ from, to, rangeLabel }) => {
  const [locations, setLocations] = useState<Location[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hover, setHover] = useState<{ loc: Location; x: number; y: number } | null>(null)
  const [zoom, setZoom] = useState<{ center: [number, number]; zoom: number }>({ center: [-96, 38], zoom: 1 })
  const [panelView, setPanelView] = useState<PanelView>('cities')
  const [selectedRow, setSelectedRow] = useState<PanelRow | null>(null)
  const [showLive, setShowLive] = useState(true)
  const [showDead, setShowDead] = useState(true)

  // Reset selection when view changes
  useEffect(() => { setSelectedRow(null) }, [panelView])

  const selectedIds = useMemo(() => {
    if (!selectedRow) return new Set<number>()
    const ids = new Set<number>()
    for (const loc of locations) {
      if (loc.latitude != null && loc.longitude != null && selectedRow.match(loc)) {
        ids.add(loc.id)
      }
    }
    return ids
  }, [selectedRow, locations])

  // Zoom-to-fit the selected locations
  useEffect(() => {
    if (!selectedRow || selectedIds.size === 0) return
    const pts: [number, number][] = []
    for (const loc of locations) {
      if (selectedIds.has(loc.id) && loc.latitude != null && loc.longitude != null) {
        pts.push([Number(loc.longitude), Number(loc.latitude)])
      }
    }
    if (pts.length === 0) return
    const lons = pts.map(p => p[0])
    const lats = pts.map(p => p[1])
    const minLon = Math.min(...lons), maxLon = Math.max(...lons)
    const minLat = Math.min(...lats), maxLat = Math.max(...lats)
    const center: [number, number] = [(minLon + maxLon) / 2, (minLat + maxLat) / 2]
    const spanLon = Math.max(0.3, maxLon - minLon)
    const spanLat = Math.max(0.3, maxLat - minLat)
    const span = Math.max(spanLon, spanLat * 1.6)
    // Rough fit: continental US ~60° wide at zoom 1; each doubling of zoom halves the view.
    const targetZoom = pts.length === 1 ? 6 : Math.max(1.5, Math.min(10, 60 / span))
    setZoom({ center, zoom: targetZoom })
  }, [selectedRow, selectedIds, locations])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const params: Record<string, string> = {}
        if (from) params.from = from
        if (to) params.to = to
        const [mapRes, statsRes] = await Promise.all([
          axios.get<{ count: number; locations: Location[] }>(`${baseUrl}/gps/map`, { params }),
          axios.get<Stats>(`${baseUrl}/gps/stats`, { params }),
        ])
        if (cancelled) return
        setLocations(mapRes.data.locations || [])
        setStats(statsRes.data)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load Geo-Analytics data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [from, to])

  const maxCalls = useMemo(
    () => locations.reduce((m, l) => Math.max(m, Number(l.calls) || 0), 0),
    [locations]
  )

  const markerRadius = (calls: number): number => {
    const base = !calls || calls <= 0
      ? 2.2
      : maxCalls <= 1
        ? 3.5
        : 2.8 + Math.sqrt(calls / maxCalls) * 8.5
    return base / Math.sqrt(zoom.zoom)
  }

  const clampZoom = (z: number) => Math.min(12, Math.max(1, z))
  const zoomIn = () => setZoom(z => ({ ...z, zoom: clampZoom(z.zoom * 1.5) }))
  const zoomOut = () => setZoom(z => ({ ...z, zoom: clampZoom(z.zoom / 1.5) }))
  const resetZoom = () => setZoom({ center: [-96, 38], zoom: 1 })

  return (
    <div className="w-full">
      {error && (
        <div className="bg-red-900/40 border border-red-800 text-red-200 text-sm rounded-lg px-4 py-3 mb-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <StatTile
          label="Live Sites on Google"
          value={formatNumber(stats?.totals.live_total || 0)}
          sub={`${formatNumber(stats?.totals.live_geocoded || 0)} on map · ${formatNumber(stats?.totals.sites_total || 0)} total`}
        />
        <StatTile
          label={`Calls${rangeLabel ? ` (${rangeLabel})` : ' (60d)'}`}
          value={formatNumber(stats?.totals.calls_total || stats?.totals.calls_60d_total || 0)}
          sub={`${formatNumber(stats?.totals.live_with_calls || 0)} sites with calls`}
        />
        <StatTile
          label="Top State"
          value={stats?.top_states?.[0]?.state || '—'}
          sub={stats?.top_states?.[0] ? `${stats.top_states[0].calls} calls · ${stats.top_states[0].live_count} sites` : undefined}
        />
        <StatTile
          label="Top Ring Group"
          value={stats?.top_ring_groups?.[0]?.ring_group || '—'}
          sub={stats?.top_ring_groups?.[0] ? `${stats.top_ring_groups[0].calls} calls · ${stats.top_ring_groups[0].live_count} sites` : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        <div className="lg:col-span-2 bg-dark-card border border-dark-border rounded-xl relative overflow-hidden">
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 bg-dark-card/80 backdrop-blur border border-dark-border rounded-md px-3 py-2">
            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showLive}
                onChange={(e) => setShowLive(e.target.checked)}
                className="accent-yellow-400 w-3.5 h-3.5"
              />
              <span className="w-2 h-2 rounded-full bg-yellow-400" />
              Live
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showDead}
                onChange={(e) => setShowDead(e.target.checked)}
                className="accent-red-500 w-3.5 h-3.5"
              />
              <span className="w-2 h-2 rounded-full bg-red-500" />
              Dead
            </label>
          </div>
          <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
            <button
              onClick={zoomIn}
              className="w-8 h-8 rounded-md bg-dark-card-hover border border-dark-border text-gray-300 hover:text-white hover:bg-dark-border transition-colors flex items-center justify-center text-lg leading-none"
              aria-label="Zoom in"
            >
              +
            </button>
            <button
              onClick={zoomOut}
              className="w-8 h-8 rounded-md bg-dark-card-hover border border-dark-border text-gray-300 hover:text-white hover:bg-dark-border transition-colors flex items-center justify-center text-lg leading-none"
              aria-label="Zoom out"
            >
              −
            </button>
            <button
              onClick={resetZoom}
              className="w-8 h-8 rounded-md bg-dark-card-hover border border-dark-border text-gray-400 hover:text-white hover:bg-dark-border transition-colors flex items-center justify-center text-[10px] uppercase tracking-wider"
              aria-label="Reset"
              title="Reset view"
            >
              ⟲
            </button>
          </div>
          <div className="relative w-full" style={{ height: 760 }}>
            <ComposableMap
              projection="geoAlbersUsa"
              projectionConfig={{ scale: 1050 }}
              width={980}
              height={600}
              style={{ width: '100%', height: '100%' }}
            >
              <defs>
                <pattern id="dotFill" patternUnits="userSpaceOnUse" width="6" height="6">
                  <circle cx="1" cy="1" r="0.8" fill="#3a3a42" />
                </pattern>
              </defs>
              <ZoomableGroup
                center={zoom.center}
                zoom={zoom.zoom}
                minZoom={1}
                maxZoom={12}
                onMoveEnd={({ coordinates, zoom: z }: any) => setZoom({ center: coordinates, zoom: z })}
              >
                <Geographies geography={US_TOPO}>
                  {({ geographies }: any) =>
                    geographies.map((geo: any) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="url(#dotFill)"
                        stroke="#5c5f6b"
                        strokeWidth={0.8 / zoom.zoom}
                        style={{
                          default: { outline: 'none' },
                          hover: { outline: 'none', fill: 'url(#dotFill)', stroke: '#7a7d8a' },
                          pressed: { outline: 'none' },
                        }}
                      />
                    ))
                  }
                </Geographies>

                {locations
                  .filter(l => l.latitude != null && l.longitude != null)
                  .filter(l => l.listed === false ? showDead : showLive)
                  .map((loc) => {
                    const hasCalls = (loc.calls || 0) > 0
                    const isUnlisted = loc.listed === false
                    const isSelected = selectedIds.has(loc.id)
                    const isDimmed = selectedRow !== null && !isSelected
                    const r = markerRadius(loc.calls) * (isSelected ? 1.4 : 1)
                    const fill = isUnlisted ? '#ef4444' : hasCalls ? '#facc15' : '#6b7280'
                    const stroke = isUnlisted ? '#fecaca' : hasCalls ? '#fde68a' : '#9ca3af'
                    const glow = isUnlisted ? '#ef4444' : '#facc15'
                    return (
                      <Marker
                        key={loc.id}
                        coordinates={[Number(loc.longitude), Number(loc.latitude)]}
                        onMouseEnter={(e: any) => setHover({ loc, x: e.clientX, y: e.clientY })}
                        onMouseMove={(e: any) => setHover({ loc, x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setHover(null)}
                        opacity={isDimmed ? 0.25 : 1}
                      >
                        {isSelected && (
                          <circle r={r + 6 / zoom.zoom} fill={glow} opacity={0.45}>
                            <animate attributeName="r" values={`${r + 6 / zoom.zoom};${r + 10 / zoom.zoom};${r + 6 / zoom.zoom}`} dur="1.8s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.45;0.15;0.45" dur="1.8s" repeatCount="indefinite" />
                          </circle>
                        )}
                        {(isUnlisted || hasCalls) && !isSelected && (
                          <circle r={r + 3 / zoom.zoom} fill={glow} opacity={0.18} />
                        )}
                        <circle
                          r={r}
                          fill={fill}
                          fillOpacity={isUnlisted || hasCalls ? 0.9 : 0.55}
                          stroke={isSelected ? '#ffffff' : stroke}
                          strokeWidth={(isSelected ? 1.4 : 0.6) / zoom.zoom}
                        />
                      </Marker>
                    )
                  })}
              </ZoomableGroup>
            </ComposableMap>

            {hover && (
              <div
                className="fixed z-50 pointer-events-none bg-dark-card border border-dark-border rounded-lg px-3 py-2 shadow-xl text-xs text-gray-200 max-w-[260px]"
                style={{ top: hover.y + 14, left: hover.x + 14 }}
              >
                <div className="font-medium text-gray-100 mb-0.5">{hover.loc.name}</div>
                {hover.loc.city && (
                  <div className="text-gray-400">{hover.loc.city}{hover.loc.state ? `, ${hover.loc.state}` : ''}</div>
                )}
                {hover.loc.phone && <div className="text-gray-500">{hover.loc.phone}</div>}
                <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                  {hover.loc.listed === false ? (
                    <span className="px-1.5 py-0.5 rounded bg-red-900/50 text-red-300">Not listed</span>
                  ) : hover.loc.listed === true ? (
                    <span className="px-1.5 py-0.5 rounded bg-green-900/40 text-green-300">Listed</span>
                  ) : null}
                  {hover.loc.rank != null && hover.loc.rank > 0 && (
                    <span className="text-gray-400">Rank #{hover.loc.rank}{hover.loc.page ? ` · p${hover.loc.page}` : ''}</span>
                  )}
                </div>
                <div className={`mt-1 font-medium ${hover.loc.listed === false ? 'text-red-300' : 'text-yellow-400'}`}>
                  {hover.loc.calls} unique caller{hover.loc.calls === 1 ? '' : 's'}{rangeLabel ? ` · ${rangeLabel}` : ' · 60d'}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1" style={{ height: 760 }}>
          <TopPanel
            view={panelView}
            onViewChange={setPanelView}
            stats={stats}
            locations={locations}
            selectedKey={selectedRow?.key ?? null}
            onSelectRow={(row) => { setSelectedRow(row); if (row === null) resetZoom() }}
          />
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-center text-sm text-gray-500">Loading Geo-Analytics...</div>
      )}
    </div>
  )
}

export default GeoAnalyticsSection
